import { Dispatch, SetStateAction, useState } from 'react';
import { QueryObserverResult } from 'react-query';

import { DownloadIcon, EyeIcon } from '../../../../assets/svg';
import { RecurrentPayIcon } from '../../../../assets/svg/payment-links-types/RecurrentPay';
import { UniquePayIcon } from '../../../../assets/svg/payment-links-types/UniquePay';
import { ETPayButton } from '../../../../components/button';
import { DateAndTime } from '../../../../components/date-and-time';
import { Select } from '../../../../components/form/select';
import { Modal } from '../../../../components/modal';
import { Spinner } from '../../../../components/spinner';
import { Status } from '../../../../components/status';
import { ETPayTable } from '../../../../components/table';
import { Tooltip } from '../../../../components/tooltip';
import { defaultTheme } from '../../../../config/theme';
import { useAuth } from '../../../../hooks/useAuth';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { PaymentStatus, Status as StatusType } from '../../../../models/common-payments';
import { PaymentMultiuse } from '../../../../models/payment-links-multiuse';
import { ViewsAllowed } from '../../../../models/payment-links-multiuse';
import { ColumnInterface, Filters, Pagination, RowInterface } from '../../../../models/table';
import { PaymentLinksMultiuseServices } from '../../../../services/payment-links-multiuse';
import { getRoleLabelById, validatePermission } from '../../../../utils/common';
import { errorFormatter } from '../../../../utils/errors';
import { fillStatesWithZeros } from '../../../../utils/states';
import { Dropdown } from '../dropdown';
import { ModalMultiuse } from '../modal';
import { NewSinglePayment } from '../new-single-payment';
import { PaymentsMultiuse, TableDetail } from '../table-detail';
import {
	AmountContainer,
	ButtonShare,
	ExportModalContent,
	ExportModalDescription,
	ExportModalTitle,
	ItemData,
	ProductFilterLabel,
	SelectContainer,
	StatusContainer,
	Text,
	TextItem,
	TextPayment,
	VoucherButton,
	VoucherContainer,
} from './styles';

interface PaymentLinkTableProps {
	data: PaymentMultiuse[];
	isLoading?: boolean;
	stateList: PaymentStatus[];
	filters: Filters;
	onFilterChange: Dispatch<SetStateAction<Filters>>;
	pagination: Pagination;
	setPagination: Dispatch<SetStateAction<Pagination>>;
	totalResults?: number;
	refetch: () => Promise<QueryObserverResult<any, unknown>>;
	voucherPdf?: boolean;
}

export interface ModalProps {
	title: string;
	message: string;
	loader: boolean;
	show: boolean;
	url: string;
}

const defaultModal: ModalProps = {
	title: 'Exportar',
	message: 'Cargando la información',
	loader: false,
	show: false,
	url: '',
};

interface ShareModalState {
	show: boolean;
	selected: string;
}

interface Status {
	status: string;
	message: string;
}

interface GetStatusMessage {
	status: string;
	numpayments: number;
	endexpiredatdate: string;
	endexpiredattime: string;
	initexpiredatdate: string;
	initexpiredattime: string;
}

const PaymentLinkTable = ({
	data,
	isLoading,
	stateList,
	filters,
	onFilterChange,
	pagination,
	setPagination,
	totalResults,
	refetch,
	voucherPdf,
}: PaymentLinkTableProps) => {
	const { user } = useAuth();
	const { merchantSelected } = useMerchant();
	const [modal, setModal] = useState<ModalProps>(defaultModal);
	const { handleShowAlert } = useFloatingAlert();
	const [showEditModal, setShowEditModal] = useState(false);
	const [selectedRowData, setSelectedRowData] = useState<PaymentMultiuse | undefined>(undefined);
	const [showShareModal, setShowShareModal] = useState<ShareModalState>({
		show: false,
		selected: '',
	});
	const [viewType, setViewType] = useState<ViewsAllowed>('create-payment');
	const [showFinalizedModal, setShowFinalizedModal] = useState(false);
	const [commentValue, setCommentValue] = useState('');

	const paymentLinkMultiuseStates = ['actived', 'pending', 'expired', 'finalized'];
	const dropdownOptions = [
		{
			label: 'Editar',
			onClick: (rowData: PaymentMultiuse) => {
				setSelectedRowData(rowData);
				setShowEditModal(true);
				setViewType('edit-payment-link');
			},
		},
		{
			label: 'Finalizar',
			onClick: (rowData: PaymentMultiuse) => {
				setSelectedRowData(rowData);
				setShowFinalizedModal(true);
			},
			colorCustom: defaultTheme.colors.extra.sword,
		},
	];

	const columns: ColumnInterface<PaymentMultiuse>[] = [
		{
			sortField: true,
			title: 'Referencia',
			propertyName: 'reference',
			cellRender: ({ merchantorderid, status }) => (
				<>
					<Text disabled={status !== 'actived' && status !== 'pending'}>{merchantorderid}</Text>
				</>
			),
		},
		{
			sortField: true,
			title: 'Monto',
			align: 'right',
			propertyName: 'amount',
			cellRender: ({ amount, status }) => (
				<AmountContainer>
					<Text disabled={status !== 'actived' && status !== 'pending'}>{amount}</Text>
				</AmountContainer>
			),
		},
		{
			sortField: true,
			title: 'Cobros exitosos',
			propertyName: 'payments',
			cellRender: ({ numsuccesspayments, status }) => (
				<>
					<Text disabled={status !== 'actived' && status !== 'pending'}>
						{numsuccesspayments || 0}
					</Text>
				</>
			),
		},
		{
			title: 'Estatus',
			propertyName: 'status',
			align: 'center',
			cellRender: ({
				status,
				numpayments,
				endexpiredatdate,
				endexpiredattime,
				initexpiredatdate,
				initexpiredattime,
			}) => {
				return (
					<>
						<PaymentStatusComponent
							status={status}
							numpayments={numpayments}
							endexpiredatdate={endexpiredatdate}
							endexpiredattime={endexpiredattime}
							initexpiredatdate={initexpiredatdate}
							initexpiredattime={initexpiredattime}
						/>
					</>
				);
			},
		},
		{
			title: 'Tipo',
			propertyName: 'paymenttype',
			cellRender: ({ paymenttype, status }) => (
				<>
					{paymenttype.toLowerCase() === 'unique' ? (
						<UniquePayIcon disabled={status !== 'actived' && status !== 'pending'} />
					) : (
						<RecurrentPayIcon disabled={status !== 'actived' && status !== 'pending'} />
					)}
				</>
			),
		},
		{
			sortField: true,
			title: 'Fecha de creación',
			propertyName: 'createdatDate',
			cellRender: ({ createdatdate, createdattime, status }) => (
				<Text disabled={status !== 'actived' && status !== 'pending'}>
					<DateAndTime date={createdatdate} time={createdattime} />
				</Text>
			),
		},
		{
			title: '',
			propertyName: 'shareButton',
			align: 'center',
			cellRender: (rowData: PaymentMultiuse) => (
				<>
					{rowData.status == 'actived' && (
						<ButtonShare
							onClick={() => {
								setSelectedRowData(rowData);
								setShowShareModal({ show: true, selected: 'send-link' });
								setViewType('share-payment-link');
							}}
						>
							Compartir
						</ButtonShare>
					)}
				</>
			),
		},
		{
			title: '',
			propertyName: '',
			cellRender: (rowData: PaymentMultiuse) => (
				<Dropdown
					disabled={rowData.status === 'actived' || rowData.status === 'pending'}
					options={dropdownOptions}
					rowData={rowData}
				/>
			),
		},
	];

	const getStatusMessage = ({
		status,
		numpayments,
		endexpiredatdate,
		endexpiredattime,
		initexpiredatdate,
		initexpiredattime,
	}: GetStatusMessage) => {
		const statusMessages: Status[] = [
			{
				status: 'finalized',
				message: 'Este enlace está inactivo y no permite recibir más pagos',
			},
			{
				status: 'actived',
				message: `Este enlace podrá recibir hasta ${numpayments || 1} pagos ${
					endexpiredatdate ? `hasta el ${endexpiredatdate} ${endexpiredattime}` : ''
				}`,
			},
			{
				status: 'pending',
				message: `Este enlace podrá recibir pagos a partir del ${initexpiredatdate} ${initexpiredattime}`,
			},
			{
				status: 'expired',
				message: 'Este enlace ha finalizado sin recibir pagos exitosos',
			},
		];

		const selectedStatus = statusMessages.find((state) => state.status === status);
		return selectedStatus ? selectedStatus.message : '';
	};

	const PaymentStatusComponent = ({
		status,
		numpayments,
		endexpiredatdate,
		endexpiredattime,
		initexpiredatdate,
		initexpiredattime,
	}: {
		status: StatusType;
		numpayments: number;
		endexpiredatdate: string;
		endexpiredattime: string;
		initexpiredatdate: string;
		initexpiredattime: string;
	}) => {
		const message = getStatusMessage({
			status,
			numpayments,
			endexpiredatdate,
			endexpiredattime,
			initexpiredatdate,
			initexpiredattime,
		});

		return (
			<StatusContainer>
				<Tooltip text={message}>
					<Status status={status} />
				</Tooltip>
			</StatusContainer>
		);
	};

	const createColumnsTableDetail = (): ColumnInterface<PaymentsMultiuse>[] => {
		const commonColumns: ColumnInterface<PaymentsMultiuse>[] = [
			{
				title: 'Identificador',
				propertyName: 'paymentId',
				cellRender: ({ paymentId }) => <TextPayment>{paymentId.slice(0, -54)}</TextPayment>,
			},
			{
				sortField: true,
				title: 'Cuenta',
				propertyName: 'clabe',
				cellRender: ({ bankName, userClabe }) => (
					<ItemData>
						<TextItem>{userClabe}</TextItem>
						<TextItem>{bankName}</TextItem>
					</ItemData>
				),
			},
			{
				title: 'Medio',
				propertyName: 'paymentMethod',
				cellRender: ({ paymentMethod, correspondent }) => (
					<ItemData>
						<TextItem>{paymentMethod}</TextItem>
						{paymentMethod?.toLowerCase() == 'efectivo' && <TextItem>{correspondent}</TextItem>}
					</ItemData>
				),
			},
			{
				title: 'Cliente',
				propertyName: 'contactInfo',
				cellRender: ({ contactInfo, userName }) => (
					<ItemData>
						<TextItem>{userName}</TextItem>
						<TextItem>{contactInfo}</TextItem>
					</ItemData>
				),
			},
			{
				title: 'Estatus',
				propertyName: 'status',
				cellRender: ({ status }) => <Status status={status} size="sm" borderFull />,
			},
			{
				sortField: true,
				title: 'F. creación',
				propertyName: 'validateatDate',
				cellRender: ({ createdatDate, createdatTime }) => (
					<DateAndTime date={createdatDate} time={createdatTime} />
				),
			},
			{
				sortField: true,
				title: 'F. expiración',
				propertyName: 'expiredatDate',
				cellRender: ({ expireDate, expireTime }) => (
					<DateAndTime date={expireDate} time={expireTime} />
				),
			},
		];

		if (voucherPdf) {
			commonColumns.push({
				title: 'Comprobante',
				align: 'center',
				propertyName: 'voucher',
				cellRender: ({ createdatDate, status, paymentId }) => (
					<VoucherContainer>
						{status == 'success' && (
							<VoucherButton onClick={() => handleExportPdf(paymentId, createdatDate)}>
								<DownloadIcon color="#646174" />
							</VoucherButton>
						)}
					</VoucherContainer>
				),
			});
		}

		if (
			merchantSelected?.provider == 'stp' ||
			merchantSelected?.provider == 'tesored' ||
			merchantSelected?.provider == null
		) {
			commonColumns.push({
				title: 'CEP',
				align: 'center',
				hide: merchantSelected?.country !== 'MX',
				propertyName: 'urlCEP',
				cellRender: ({ urlCEP }) => (
					<VoucherContainer>
						{urlCEP && (
							<VoucherButton onClick={() => window.open(urlCEP)}>
								<EyeIcon />
							</VoucherButton>
						)}
					</VoucherContainer>
				),
			});
		}

		const lastStepColumn: ColumnInterface<PaymentsMultiuse> = {
			title: 'Ultimo paso',
			propertyName: 'lastStep',
			cellRender: ({ lastStep }) => <>{lastStep}</>,
		};

		const isETpay =
			merchantSelected &&
			(getRoleLabelById(merchantSelected?.role_id, merchantSelected?.role_id) === 'ETpay' ||
				user?.email.includes('@etpay'));

		if (isETpay) {
			return [...commonColumns.slice(0, 7), lastStepColumn, ...commonColumns.slice(7)];
		}

		return commonColumns;
	};

	const columnsTableDetail = createColumnsTableDetail();

	const tableWithDetails: RowInterface<PaymentMultiuse> = (data) => {
		return data.payments.length ? (
			<TableDetail columns={columnsTableDetail} data={data.payments} />
		) : undefined;
	};

	const handleExportAction = async () => {
		setModal({ ...modal, loader: true, show: true });

		try {
			const response = await PaymentLinksMultiuseServices.getExportData({
				merchantCode: merchantSelected?.code as string,
				dateRange: {
					from: filters?.dateRange?.from,
					to: filters?.dateRange?.to,
				},
				search: filters?.search,
				stateSelected: filters?.stateSelected,
			});

			if (response.url) setModal({ ...modal, loader: false, show: true, url: response.url });
		} catch (e: any) {
			handleShowAlert({
				type: 'error',
				message: errorFormatter(e.code),
			});
		}
	};

	const handleExportPdf = async (token: string, date: string) => {
		await setModal({
			...modal,
			loader: true,
			show: true,
			title: 'Descargar',
			message: 'Generando comprobante',
		});

		try {
			const response = await PaymentLinksMultiuseServices.getExportPdf({
				merchantCode: merchantSelected?.code as string,
				token: token,
				date: date,
				origin: 'payments',
			});

			window.open(response.metadata.pdf, '_blank');
			if (response.metadata.pdf)
				setModal({
					...modal,
					loader: false,
					show: false,
					title: 'Exportar',
					message: 'Cargando la información',
				});
		} catch (e: any) {
			handleShowAlert({
				type: 'error',
				message: errorFormatter(e.code),
			});

			setModal({
				...modal,
				loader: false,
				show: false,
				title: 'Exportar',
				message: 'Cargando la información',
			});
		}
	};
	const handleCloseModal = () => {
		setModal(defaultModal);
	};

	const handleDownloadData = () => {
		if (modal.url) window.location.assign(modal.url);
		setModal(defaultModal);
	};

	const onClose = () => {
		setShowEditModal(false);
		setShowShareModal({ show: false, selected: '' });
		setSelectedRowData(undefined);
	};

	const handleEndPaymentLink = async () => {
		setModal((modal) => ({ ...modal, loader: true, show: true }));
		try {
			await PaymentLinksMultiuseServices.endPaymentLinkMultiuse({
				merchantCode: merchantSelected?.code as string,
				presessionToken: selectedRowData?.token.split('/').slice(-1)[0] || '',
				comment: commentValue,
			});
			setShowFinalizedModal(false);
			refetch();
			handleShowAlert({
				message: 'Enlace de cobro finalizado',
				type: 'success',
			});
			setModal((modal) => ({ ...modal, loader: false, show: false }));
		} catch (e: any) {
			handleShowAlert({
				message: errorFormatter(e.code),
				type: 'error',
			});
		}
	};

	const renderSelectByType = () => (
		<SelectContainer>
			<Select
				options={[
					{ label: 'Todos', value: '', customLabel: 'Todos', show: true },
					{
						label: (
							<ProductFilterLabel>
								<UniquePayIcon /> Únicos
							</ProductFilterLabel>
						),
						value: 'UNIQUE',
						customLabel: 'Únicos',
						show: true,
					},
					{
						label: (
							<ProductFilterLabel>
								<RecurrentPayIcon /> Múltiples
							</ProductFilterLabel>
						),
						value: 'RECURRENT',
						customLabel: 'Múltiples',
						show: true,
					},
				]}
				prefix="Tipo:"
				onChange={({ value }) => {
					if (onFilterChange) {
						onFilterChange({ ...filters, typeSelected: value as string });
					}
				}}
			/>
			{merchantSelected?.country == 'MX' && (
				<Select
					options={[
						{ label: 'Todos', value: '', customLabel: 'Todos', show: true },
						{
							label: <ProductFilterLabel>SPEI</ProductFilterLabel>,
							value: 'spei',
							customLabel: 'SPEI',
							show: true,
						},
						{
							label: <ProductFilterLabel> CoDi</ProductFilterLabel>,
							value: 'codi',
							customLabel: 'CoDi',
							show: true,
						},
						{
							label: <ProductFilterLabel> Efectivo</ProductFilterLabel>,
							value: 'efectivo',
							customLabel: 'Efectivo',
							show: true,
						},
					]}
					prefix="Medio de pago:"
					onChange={({ value }) => {
						if (onFilterChange) {
							onFilterChange({ ...filters, method: value as string });
						}
					}}
				/>
			)}
		</SelectContainer>
	);
	return (
		<>
			{modal.show && (
				<Modal
					title={modal.title}
					show={modal.show}
					onClose={handleCloseModal}
					allowCloseModal={!modal.loader}
				>
					{modal.loader ? (
						<Spinner message={modal.message} />
					) : (
						<ExportModalContent>
							<ExportModalTitle>Tu reporte está listo</ExportModalTitle>

							<ExportModalDescription>
								Hemos terminado de recopilar los datos de tu reporte. Haz clic en el botón para
								descargarlo.
							</ExportModalDescription>
							<ETPayButton
								title="Descargar reporte"
								variant="effect"
								onClick={handleDownloadData}
							/>
						</ExportModalContent>
					)}
				</Modal>
			)}
			{(showShareModal.show || showEditModal) && (
				<NewSinglePayment
					show={showEditModal || showShareModal.show}
					onClose={onClose}
					selectedToEdit={selectedRowData}
					showShareModal={showShareModal}
					viewType={viewType}
					refetch={refetch}
				/>
			)}
			{showFinalizedModal && (
				<ModalMultiuse
					show={showFinalizedModal}
					setShowFinalizedModal={() => setShowFinalizedModal(false)}
					handleEndPaymentLink={() => handleEndPaymentLink()}
					setCommentValue={setCommentValue}
					commentValue={commentValue}
					modal={modal}
				/>
			)}

			<ETPayTable<PaymentMultiuse>
				title="Todos los enlaces de cobro"
				data={data}
				isLoading={!!isLoading}
				columns={columns}
				row={tableWithDetails}
				filtersMobil={merchantSelected?.country != 'MX'}
				customFilter={renderSelectByType()}
				hasFilters
				hasDateFilter
				allowExport={validatePermission('PL_EXPORT')}
				stateAll={false}
				stateList={fillStatesWithZeros({ states: paymentLinkMultiuseStates, stateList })}
				filters={filters}
				onFilterChange={onFilterChange}
				hasSearch
				exportAction={handleExportAction}
				hasPagination
				allowCustomFilter
				totalResults={totalResults}
				pagination={pagination}
				setPagination={setPagination}
				statusContent={[
					['actived', 'Activos'],
					['pending', 'Pendiente'],
					['finalized', 'Finalizado'],
					['expired', 'Expirados'],
				]}
			/>
		</>
	);
};

export { PaymentLinkTable };
